"use client"

import { useEffect } from "react"
import { usePathname, useRouter } from "next/navigation"
import { useTheme } from "next-themes"
import { App as CapApp } from "@capacitor/app"
import { Browser } from "@capacitor/browser"
import { SplashScreen } from "@capacitor/splash-screen"
import { StatusBar, Style } from "@capacitor/status-bar"
import { Keyboard, KeyboardResize } from "@capacitor/keyboard"
import { createClient } from "@/lib/supabase/client"
import { isNativeApp } from "@/lib/native"
import { attachPushListeners, forgetDevice, initPush } from "@/lib/push/register-device"
import { stripLocalePrefix, isGlassNavPath } from "@/i18n/routing"

// Everything the site does differently when it is running inside the iOS /
// Android shell. Mounted once in the root layout; a no-op in browsers.
//
//  - Splash: hidden once the first page has painted.
//  - Status bar: light text over dark pages (auth, marketing glass), dark
//    text over the light app, and it follows the in-app theme toggle.
//  - Keyboard: the web view frame resizes (native mode) so a focused field
//    scrolls into view above the keyboard. The message composer keeps body
//    mode, where it rises with the keyboard instead.
//  - Sign-in return: Google sign-in opens in the system browser (Google
//    blocks OAuth inside web views), Supabase sends the result to our custom
//    scheme, and the code is exchanged here, in the web view's own session.
//  - Back button (Android): browser history, then background the app.
export function NativeShell() {
  const pathname = usePathname()
  const router = useRouter()
  const { resolvedTheme } = useTheme()

  useEffect(() => {
    if (!isNativeApp()) return
    void SplashScreen.hide({ fadeOutDuration: 200 })
  }, [])

  // Keyboard resize mode, per route.
  //
  // Body mode sets the BODY's height and leaves the web view frame alone. That
  // is what the message composer wants — it rises with the keyboard — but it
  // breaks any page taller than the space left over, and signup is one:
  //
  //   document.scrollingElement is <html>, and in Body mode <html> keeps the
  //   full viewport height. Measured on the real page with the body shrunk to
  //   380px: scrollHeight 966, clientHeight 966, canScroll FALSE. There is no
  //   scrollable overflow for a gesture to act on, so the fields below the
  //   keyboard cannot be reached at all. 100dvh does not help — it resolves
  //   against the viewport, which Body mode does not change, so it still
  //   reported 966px.
  //
  // Native mode resizes the web view frame instead. innerHeight and dvh then
  // track the space above the keyboard, the page overflows <html> normally,
  // and WebKit scrolls the focused field into view on its own.
  //
  // So Native is the default and Body is the exception, which is the way
  // round this should always have been. Body was chosen for ONE surface — the
  // message composer, which is pinned to the bottom and rises with the
  // keyboard — and every other screen inherited it by accident. On those
  // screens Body's failure is total rather than cosmetic: anything below the
  // fold while the keyboard is up cannot be reached by any gesture.
  //
  // What that cost: the Contact Shop sheet on a listing opens with its
  // heading visible and its textarea behind the keyboard, so the one action
  // the whole listing page exists for — message the owner — could not be
  // completed on a phone. /auth had already been carved out for the same
  // reason when signup hit it; this generalises that fix instead of adding a
  // third special case.
  useEffect(() => {
    if (!isNativeApp()) return
    const path = stripLocalePrefix(pathname ?? "/")
    // Every messages surface: /dashboard/messages and the per-mode ones.
    const isComposer = path.includes("/messages")
    const mode = isComposer ? KeyboardResize.Body : KeyboardResize.Native
    void Keyboard.setResizeMode({ mode }).catch(() => {})
  }, [pathname])

  // --keyboard-h is NOT published here. The resize modes above already take
  // the keyboard out of the viewport, so adding the plugin's height on top of
  // that lifted bottom-anchored sheets by two keyboards. ViewportInsets
  // measures what is actually left of the visual viewport instead, in the app
  // as well as the browser — see components/viewport-insets.tsx.

  // Push listeners, attached once and early.
  //
  // Early because the "registration" listener is how the APNs token reaches
  // the server, and register() can answer immediately for a device that was
  // already granted permission — attach after that and the token is simply
  // lost until the next launch.
  //
  // Nothing here prompts. The ask lives on the notifications page
  // (components/native/push-permission-card.tsx), where the person has already
  // said what they want; iOS only allows one ask, so it is not spent on launch.
  useEffect(() => {
    if (!isNativeApp()) return
    let detach: (() => void) | undefined
    let cancelled = false

    void attachPushListeners({
      onOpen: (path) => router.push(path),
    }).then((teardown) => {
      if (cancelled) teardown()
      else detach = teardown
    })

    return () => {
      cancelled = true
      detach?.()
    }
  }, [router])

  // Follow the session, so this device is always registered to whoever is
  // actually signed in on it.
  //
  // Sign-out happens in five different components today, and hooking each one
  // means missing the sixth — the same reason 1032 puts push on a database
  // trigger rather than in the 62 places that write a notification. The auth
  // state is the one thing every path goes through.
  //
  // Signing in registers rather than prompts: a phone that already granted
  // permission gets its token attached to the new account (push_tokens moves
  // the row, so the previous account stops receiving on this device), and a
  // phone that has not is left alone until the notifications page asks.
  useEffect(() => {
    if (!isNativeApp()) return
    const supabase = createClient()
    const { data } = supabase.auth.onAuthStateChange((event: string) => {
      if (event === "SIGNED_IN") void initPush({ prompt: false })
      if (event === "SIGNED_OUT") void forgetDevice()
    })
    return () => data.subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!isNativeApp()) return
    const path = stripLocalePrefix(pathname ?? "/")
    const darkPage =
      path === "/" || path.startsWith("/auth") || path === "/clients" || path.startsWith("/search") || isGlassNavPath(path)
    const dark = darkPage || resolvedTheme === "dark"
    void StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => {})
  }, [pathname, resolvedTheme])

  useEffect(() => {
    if (!isNativeApp()) return
    const sub = CapApp.addListener("appUrlOpen", async ({ url }) => {
      let parsed: URL
      try {
        parsed = new URL(url)
      } catch {
        return
      }
      // co.boothapp.app://auth/callback?code=…  (host "auth", path "/callback")
      if (parsed.protocol === "co.boothapp.app:" && `${parsed.host}${parsed.pathname}` === "auth/callback") {
        void Browser.close().catch(() => {})
        const code = parsed.searchParams.get("code")
        const next = parsed.searchParams.get("next") || "/auth/native"
        if (!code) {
          router.replace(`/auth/login?message=${encodeURIComponent(parsed.searchParams.get("error_description") || "Sign in was cancelled.")}`)
          return
        }
        const supabase = createClient()
        const { error } = await supabase.auth.exchangeCodeForSession(code)
        if (error) {
          router.replace(`/auth/login?message=${encodeURIComponent(error.message)}`)
          return
        }
        // Full navigation, not a client transition: /auth/native is a route
        // handler that needs the fresh session cookie on the request.
        window.location.assign(next)
        return
      }
      // Universal links (https://www.boothapp.co/...) just navigate.
      if (parsed.hostname.endsWith("boothapp.co")) router.push(parsed.pathname + parsed.search)
    })
    const back = CapApp.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) window.history.back()
      else void CapApp.minimizeApp()
    })
    return () => {
      void sub.then((h) => h.remove())
      void back.then((h) => h.remove())
    }
  }, [router])

  return null
}
